/**
 * One workspace on the Workspaces card: a header row that expands into the
 * policy radio (saves on change, with Undo), the four Serving-now tiles the
 * router would resolve this minute, and a one-row pin form.
 */

import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../../../ui/button.js";
import type {
  CatalogModel,
  Lane,
  LaneState,
  Residency,
  RoutedWorkspace,
  RoutingPolicy,
  Tier,
  WorkspaceRoutesResponse,
} from "./types.js";
import { LANE_NAMES, formatDate, pinnableLanes, policyName } from "./format.js";
import { JSON_HEADERS, describeError, responseError, runMutation } from "./http.js";
import { ServingTiles } from "./ServingTiles.js";
import { PinForm } from "./PinForm.js";

interface WorkspaceItemProps {
  workspace: RoutedWorkspace;
  catalog: Record<Lane, CatalogModel[]>;
  lanes: LaneState[];
  policy: RoutingPolicy;
  tiers: Tier[];
  open: boolean;
  onToggle: () => void;
  /** Present when the workspace was opened from search and follows the defaults. */
  onClose?: () => void;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}

const GRID = "grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-[1.6fr_110px_1.2fr_1.6fr_90px]";
const SECTION_LABEL_CLASS = "mb-1.5 text-[10px] uppercase tracking-wider";
const SECTION_LABEL_STYLE = { color: "var(--text-label)", fontFamily: "var(--font-mono)" } as const;

function PolicyPill({ residency }: { residency: Residency }) {
  const us = residency === "us";
  return (
    <span
      className="inline-block rounded-[var(--radius-pill)] border px-2 py-0.5 text-[11px]"
      style={{
        borderColor: us ? "var(--accent-border)" : "var(--border-subtle)",
        background: us ? "var(--accent-muted)" : "transparent",
        color: us ? "var(--accent)" : "var(--text-secondary)",
        fontFamily: us ? "var(--font-mono)" : undefined,
      }}
    >
      {policyName(residency)}
    </span>
  );
}

export function WorkspaceItem({
  workspace,
  catalog,
  lanes,
  policy,
  tiers,
  open,
  onToggle,
  onClose,
  onChanged,
  onError,
}: WorkspaceItemProps) {
  const [detail, setDetail] = useState<WorkspaceRoutesResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [undoTo, setUndoTo] = useState<{ residency: Residency } | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);

  const id = workspace.workspaceId;
  const bodyId = `workspace-routing-${id}`;

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`/api/admin/tier-models/workspaces/${encodeURIComponent(id)}`);
      if (!res.ok) {
        setDetailError(`Unable to load this workspace's routes: ${await responseError(res)}`);
        return;
      }
      setDetail((await res.json()) as WorkspaceRoutesResponse);
      setDetailError(null);
    } catch (err) {
      setDetailError(describeError(err, "Unable to load this workspace's routes"));
    } finally {
      setLoadingDetail(false);
    }
  }, [id]);

  useEffect(() => {
    if (open) void loadDetail();
    else setUndoTo(null);
  }, [open, loadDetail]);

  const residency: Residency = detail?.workspace.dataResidency ?? workspace.dataResidency;
  const pins = detail?.pins ?? workspace.pins;
  const order = residency === "us" ? policy.us : policy.unrestricted;
  // A lane with no state row counts as serving, as in the router.
  const isServing = (lane: Lane) => lanes.find((l) => l.lane === lane)?.serving ?? true;
  const servingOrder = order.filter(isServing);
  const since = pins.length
    ? formatDate(pins.map((p) => p.createdAt).sort()[0] ?? "")
    : "—";

  /** Refetch this item's routes and the page after a successful change. */
  const reload = async () => {
    await Promise.all([loadDetail(), onChanged()]);
  };

  async function changePolicy(next: Residency, undo: boolean) {
    if (next === residency) return;
    const previous = residency;
    setSavingPolicy(true);
    await runMutation(
      () =>
        fetch("/api/admin/tier-models/residency", {
          method: "PUT",
          headers: JSON_HEADERS,
          body: JSON.stringify({ workspaceId: id, residency: next }),
        }),
      "Unable to change the workspace policy",
      {
        onError,
        onSuccess: async () => {
          setDetail((d) => (d ? { ...d, workspace: { ...d.workspace, dataResidency: next } } : d));
          setUndoTo(undo ? null : { residency: previous });
          await reload();
        },
      },
    );
    setSavingPolicy(false);
  }

  async function releasePin(pinId: string) {
    setReleasing(pinId);
    await runMutation(
      () => fetch(`/api/admin/model-pins/${encodeURIComponent(pinId)}/release`, { method: "POST" }),
      "Unable to release the pin",
      { onError, onSuccess: reload },
    );
    setReleasing(null);
  }

  const pinsSummary =
    pins.length === 0
      ? "No pins"
      : `${pins.length} ${pins.length === 1 ? "pin" : "pins"}: ${pins.map((p) => p.tier).join(", ")}`;

  return (
    <div
      className="rounded-[var(--radius-control)] border"
      style={{
        borderColor: open ? "var(--accent-border)" : "var(--border-subtle)",
        background: "rgba(var(--glass-rgb), .6)",
      }}
      data-testid={`workspace-item-${workspace.slug}`}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
        className={`${GRID} w-full items-center px-3 py-2 text-left text-[13px]`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <span className="truncate font-medium" style={{ color: "var(--text-primary)" }}>
            {workspace.name}
          </span>
          <span className="truncate text-[11px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
            {workspace.slug}
          </span>
        </span>
        <span>
          <PolicyPill residency={workspace.dataResidency} />
        </span>
        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {pinsSummary}
        </span>
        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {servingOrder.map((l) => LANE_NAMES[l]).join(" → ") || "No lane serving"}
        </span>
        <span className="text-xs" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
          {since}
        </span>
      </button>

      {open && (
        <div id={bodyId} className="flex flex-col gap-3 px-3 pb-3 pt-1" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          {detailError && (
            <div className="pt-2 text-xs" role="alert" style={{ color: "var(--error)" }}>
              {detailError}
            </div>
          )}

          {/* Policy */}
          <fieldset className="flex flex-wrap items-center gap-4 pt-2 text-[13px]">
            <legend className="sr-only">{`Policy for ${workspace.name}`}</legend>
            <span className="text-[10px] uppercase tracking-wider" style={SECTION_LABEL_STYLE}>
              Policy
            </span>
            {([null, "us"] as const).map((value) => (
              <label key={value ?? "unrestricted"} className="flex items-center gap-1.5" style={{ color: "var(--text-primary)" }}>
                <input
                  type="radio"
                  name={`policy-${id}`}
                  value={value ?? "unrestricted"}
                  checked={residency === value}
                  disabled={savingPolicy || !detail}
                  onChange={() => void changePolicy(value, false)}
                  style={{ accentColor: "var(--accent)" }}
                />
                {policyName(value)}
              </label>
            ))}
            {savingPolicy && <Loader2 size={14} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />}
            {undoTo && !savingPolicy && (
              <span className="text-xs" role="status" style={{ color: "var(--success)" }}>
                Saved · live within a minute.{" "}
                <button
                  type="button"
                  className="underline"
                  style={{ color: "var(--accent)" }}
                  onClick={() => void changePolicy(undoTo.residency, true)}
                >
                  Undo
                </button>
              </span>
            )}
          </fieldset>

          {/* Serving now */}
          <div>
            <div className={SECTION_LABEL_CLASS} style={SECTION_LABEL_STYLE}>
              Serving now
            </div>
            {detail ? (
              <ServingTiles
                detail={detail}
                catalog={catalog}
                tiers={tiers}
                order={order}
                residency={residency}
                releasing={releasing}
                onRelease={(pinId) => void releasePin(pinId)}
              />
            ) : loadingDetail ? (
              <Loader2 size={16} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
            ) : null}
          </div>

          {/* Pin a tier */}
          <div>
            <div className={SECTION_LABEL_CLASS} style={SECTION_LABEL_STYLE}>
              Pin a tier
            </div>
            <PinForm
              workspaceId={id}
              tiers={tiers}
              permittedLanes={pinnableLanes(order, policy.pinOnly, residency)}
              catalog={catalog}
              disabled={!detail}
              onPinned={reload}
              onError={onError}
              trailing={
                onClose && (
                  <Button type="button" size="sm" variant="secondary" onClick={onClose}>
                    Close
                  </Button>
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
